import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../shared-components/PageHeader";
import { Fragment, useEffect, useState } from "react";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";

import {
  fetchAllWorkflowStepRunsForDebug,
  useReportByUUID,
} from "../api/ReportsApi";
import { useAppContext } from "../AppProviders";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { WorkflowStepRun } from "../api/Workflows";
import { useReportAuxiliaryLeaveUnlock } from "./useReportAuxiliaryLeaveUnlock";

export interface WorkflowDebugRow {
  id: number;
  created_at: string;
  workflow_run_id: number;
  name: string;
  started_at: string;
  finished_at: string;
  expired_at: string;
  retry_nb: number;
  status: string;
  error_type: null | string;
  error_message: null | string;
  duration: number;
  gap: number;
  percent_start: number;
  percent_end: number;
  payload: WorkflowStepRun["payload"];
}

function renderStepBranch(
  sr: WorkflowDebugRow,
  nestedSteps: { [key: number]: WorkflowDebugRow[] },
  parentSteps: { [key: number]: number },
  depth: number,
  ancestorStepRunIds: Set<number>
): JSX.Element {
  // Only steps that list this step run as parent; otherwise when childRunId
  // equals sr.workflow_run_id we would include the whole run (and sr itself)
  // and recurse until stack overflow.
  const childRunId = parentSteps[sr.id];
  const candidateRows = childRunId != null ? nestedSteps[childRunId] ?? [] : [];
  const childRows = candidateRows.filter(
    (ch) => ch.payload?.parent_workflow_step_run_id === sr.id
  );

  const nextAncestors = new Set(ancestorStepRunIds);
  nextAncestors.add(sr.id);

  return (
    <>
      <tr className={depth > 0 ? "subworkflow" : undefined}>
        <td>{sr.name}</td>
        <td>{sr.retry_nb}</td>
        <td>{sr.status}</td>
        <td>{sr.gap}</td>
        <td>{sr.duration}</td>
        <td>{sr.created_at}</td>
        <td>{sr.finished_at}</td>
        <td
          className="timeline"
          style={{
            paddingLeft: `${sr.percent_start * 1000}px`,
            paddingRight: `${sr.percent_end * 1000}px`,
          }}
        >
          <div>&nbsp;</div>
        </td>
      </tr>
      {childRows
        .filter((ch) => !nextAncestors.has(ch.id))
        .map((ch) => (
          <Fragment key={ch.id}>
            {renderStepBranch(
              ch,
              nestedSteps,
              parentSteps,
              depth + 1,
              nextAncestors
            )}
          </Fragment>
        ))}
    </>
  );
}

const WorkflowDebug = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestApi } = useAppContext();
  const [nestedSteps, setNestedSteps] = useState<{
    [key: number]: WorkflowDebugRow[];
  }>({});
  const [parentSteps, setParentSteps] = useState<{ [key: number]: number }>({});
  const [stepRunsData, setStepRunsData] = useState<
    WorkflowStepRun[] | undefined
  >(undefined);
  const [stepRunsLoading, setStepRunsLoading] = useState(false);
  const [stepRunsError, setStepRunsError] = useState(false);
  const [stepRunsErr, setStepRunsErr] = useState<unknown>(null);
  const {
    isLoading: reportLoading,
    isError: reportError,
    data: report,
    mutate: reportGet,
    error: reportErr,
  } = useReportByUUID();
  const { modal, errorUnlockingReport } = useReportAuxiliaryLeaveUnlock({
    report,
    isReportError: reportError,
    isReportLoading: reportLoading,
  });

  useEffect(() => {
    if (id) {
      reportGet(id);
    }
  }, [reportGet, id]);

  useEffect(() => {
    let cancelled = false;
    if (!report?.workflow_run_uuid || !requestApi) {
      setStepRunsData(undefined);
      setStepRunsLoading(false);
      setStepRunsError(false);
      setStepRunsErr(null);
      return;
    }
    setStepRunsLoading(true);
    setStepRunsError(false);
    setStepRunsErr(null);
    fetchAllWorkflowStepRunsForDebug(requestApi, report.workflow_run_uuid)
      .then((rows) => {
        if (!cancelled) {
          setStepRunsData(rows);
          setStepRunsLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setStepRunsError(true);
          setStepRunsErr(e);
          setStepRunsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestApi, report?.workflow_run_uuid]);

  useEffect(() => {
    setNestedSteps({});
    setParentSteps({});
    if (!stepRunsData?.length) {
      return;
    }
    const sorted = [...stepRunsData].sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    const newSteps: { [key: number]: WorkflowDebugRow[] } = {};
    const newParents: { [key: number]: number } = {};
    let prevEndMs: number | null = null;

    const startTimes = sorted.map((x) => new Date(x.created_at).getTime());
    const minTime = Math.min(...startTimes);
    const finishedMsList = sorted
      .map((sr) =>
        sr.finished_at != null ? new Date(sr.finished_at).getTime() : null
      )
      .filter((t): t is number => t != null);
    const maxFromFinished =
      finishedMsList.length > 0 ? Math.max(...finishedMsList) : minTime;
    const maxTime = Math.max(maxFromFinished, ...startTimes);
    const delta = Math.max(maxTime - minTime, 1);

    sorted.forEach((sr) => {
      const key = sr.workflow_run_id;
      const parent = sr.payload?.parent_workflow_step_run_id ?? 0;
      if (parent !== sr.id && !newParents[parent]) {
        newParents[parent] = key;
      }
      const temp: WorkflowDebugRow[] = newSteps[key] || [];
      const createdMs = new Date(sr.created_at).getTime();
      let gap = 0;
      if (prevEndMs != null) {
        gap = (createdMs - prevEndMs) / 1000;
      }
      const finishedMs =
        sr.finished_at != null ? new Date(sr.finished_at).getTime() : null;
      const percent_start = (createdMs - minTime) / delta;
      let percent_end = 0;
      if (finishedMs != null) {
        percent_end = (maxTime - finishedMs) / delta;
      }
      const duration = finishedMs != null ? (finishedMs - createdMs) / 1000 : 0;
      const newRow: WorkflowDebugRow = {
        ...sr,
        duration,
        gap,
        percent_start,
        percent_end,
      };
      temp.push(newRow);
      newSteps[key] = temp;
      if (finishedMs != null) {
        prevEndMs = finishedMs;
      }
    });
    setNestedSteps(newSteps);
    setParentSteps(newParents);
  }, [stepRunsData]);

  const goLogs = () => {
    navigate(`/reports/${id}/logs`, {
      state: {
        workflowRunUuid: report?.workflow_run_uuid,
        fromPath: `${location.pathname}${location.search}`,
      },
    });
  };

  const rootWorkflowRunId =
    parentSteps[0] ??
    (Object.keys(nestedSteps).length
      ? Number(Object.keys(nestedSteps)[0])
      : undefined);
  const rootRows =
    rootWorkflowRunId != null ? nestedSteps[rootWorkflowRunId] : undefined;

  if (reportLoading) {
    return (
      <>
        {modal}
        <LoadingSpinner />
      </>
    );
  }

  const errMsg = (e: unknown) =>
    e && typeof e === "object" && "message" in e
      ? String((e as { message?: string }).message)
      : null;

  if (reportError) {
    return (
      <div className="p-4">
        {modal}
        <PageHeader title="Workflow Debug" />
        <p className="text-red-600">
          Could not load report summary.
          {errMsg(reportErr) ? ` ${errMsg(reportErr)}` : ""}
        </p>
      </div>
    );
  }

  if (errorUnlockingReport) {
    return (
      <>
        {modal}
        <ErrorMessageContainer message="Error Unlocking Report" />
      </>
    );
  }

  return (
    <div>
      {modal}
      <Button
        text="Logs"
        onClick={goLogs}
        colorType={ButtonColorTypes.custom}
        type={ButtonTypes.back}
        className="w-20	h-20 max-w-xs max-h-9 px-0 items-start my-1"
      />
      <PageHeader title={"Workflow Debug"}></PageHeader>
      {stepRunsLoading ? (
        <LoadingSpinner />
      ) : stepRunsError ? (
        <p className="text-red-600">
          Could not load workflow steps.
          {errMsg(stepRunsErr) ? ` ${errMsg(stepRunsErr)}` : ""}
        </p>
      ) : (
        <div>
          <table className="workflow-debug">
            <thead>
              <tr>
                <th>Name</th>
                <th>Retry</th>
                <th>Status</th>
                <th>Gap</th>
                <th>Duration</th>
                <th>Start</th>
                <th>End</th>
                <th className="timeline">Timeline</th>
              </tr>
            </thead>
            {rootRows?.map((sr) => (
              <tbody key={sr.id}>
                {renderStepBranch(sr, nestedSteps, parentSteps, 0, new Set())}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>
  );
};

export default WorkflowDebug;
